/**
 * Marges et pied des feuilles imprimées de la gamme et du bon d'intervention (lot I2.2).
 *
 * Pourquoi une règle `@page` AJOUTÉE à la page quand elle s'affiche, et non des pages nommées (`page: gamme`) dans
 * la feuille de styles : le 2026-10-14, un PDF enregistré depuis Edge 155 a montré que les deux premières feuilles
 * gardaient leurs marges (12 mm) et que les suivantes retombaient sur la règle `@page` SANS NOM de l'application
 * (marge 0, faite pour les planches d'étiquettes) : texte collé au bord de la feuille, donc coupé à l'impression.
 * La règle ordinaire s'applique à TOUTES les pages, sans passer par l'association page → nom que ce navigateur
 * n'a pas tenue au-delà de la deuxième feuille.
 *
 * La règle n'existe que tant que la page d'impression de la gamme (ou du bon) est affichée : la planche d'étiquettes QR
 * garde ses marges à 0. Le bon suit le même principe avec `installerPageBon`, et son propre attribut : les deux règles ne se
 * retirent pas l'une l'autre.
 */
import { MENTION_NON_VALIDE } from './bon-document';
import { AVERTISSEMENT_INTERNE } from './gammes-document';

/**
 * Ce que le NAVIGATEUR ajoute de lui-même sur la feuille (titre de la page et adresse du site en haut, numéro de page et
 * date en bas) quand son option d'impression « En-têtes et pieds de page » est cochée : constaté sur le PDF de Benjamin du
 * 2026-10-17 (« il reste tes écritures sur le haut de la page »). Aucune règle de la feuille ne peut l'éteindre sans perdre
 * les marges de chaque page : seule l'option du navigateur le fait, et le navigateur s'en souvient ensuite. D'où ce conseil,
 * affiché au-dessus du bouton d'impression (hors de la feuille).
 */
export const CONSEIL_EN_TETES_NAVIGATEUR =
  'Pour un papier propre, décochez « En-têtes et pieds de page » (sous « Plus de paramètres ») dans la fenêtre d’impression : sinon le navigateur ajoute lui-même le titre, l’adresse du site, la date et le numéro de page sur la feuille.';

/** Haut, côtés, bas : le bas est un peu plus haut pour le numéro de page. */
export const MARGES_FEUILLE_GAMME = '14mm 12mm 16mm';

/**
 * La règle `@page` de la feuille de gamme. Le numéro de page et l'avertissement « interne » en marge ne
 * s'impriment que là où le navigateur sait écrire dans la marge, et quand ses propres en-têtes et pieds de
 * page sont coupés : l'avertissement est de toute façon répété en tête de chaque palier, dans la page.
 */
export function cssPageGamme(interne: boolean): string {
  const avertissement = interne
    ? `@top-center { content: '${AVERTISSEMENT_INTERNE}'; font-size: 10px; font-weight: 700; color: #b3261e; }`
    : '';
  return [
    '@page {',
    '  size: A4 portrait;',
    `  margin: ${MARGES_FEUILLE_GAMME};`,
    avertissement && `  ${avertissement}`,
    "  @bottom-right { content: 'Page ' counter(page) ' / ' counter(pages); font-size: 9px; color: #666; }",
    '}',
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Marges de la feuille du bon : plus étroites que celles de la gamme, pour tenir sur UNE page (8 mm en haut et en bas, 10 mm sur
 * les côtés : la zone de contenu fait 190 mm de large, comme l'ébauche validée par Benjamin le 2026-10-16).
 */
export const MARGES_FEUILLE_BON = '8mm 10mm 8mm';

/**
 * Les boîtes de marge (mention, numéro de page) n'héritent PAS de la police de la feuille : sans police nommée elles sortent
 * en Times, qui s'embarque en plus dans le PDF — du poids pour rien (un bon pèse moins de 150 Ko, règle de Benjamin).
 */
export const POLICE_DES_MARGES = 'Arial, Helvetica, sans-serif';

/**
 * La règle `@page` de la feuille du bon : A4 portrait, de vraies marges sur CHAQUE côté (une marge à 0 colle le texte au
 * bord de la feuille, où il est coupé), le numéro de page en marge. Un bon qui n'est pas encore validé porte en plus la
 * mention « BON NON VALIDÉ » en marge de chaque page : une photocopie de la 2ᵉ page ne passe pas pour un bon définitif.
 * Comme pour la gamme : une règle ordinaire, jamais une page nommée.
 */
export function cssPageBon(valide: boolean): string {
  const mention = valide
    ? ''
    : `@top-center { content: '${MENTION_NON_VALIDE}'; font-family: ${POLICE_DES_MARGES}; font-size: 10px; font-weight: 700; color: #b3261e; }`;
  return [
    '@page {',
    '  size: A4 portrait;',
    `  margin: ${MARGES_FEUILLE_BON};`,
    mention && `  ${mention}`,
    `  @bottom-right { content: 'Page ' counter(page) ' / ' counter(pages); font-family: ${POLICE_DES_MARGES}; font-size: 9px; color: #666; }`,
    '}',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Marges de la fiche de préparation (lot PL4) : A4 portrait, un peu plus de place en bas pour le numéro de page. */
export const MARGES_FEUILLE_FICHE = '10mm 12mm 14mm';

/**
 * La règle `@page` de la fiche de préparation : A4 portrait, de vraies marges sur CHAQUE côté, le numéro de page en marge. Comme pour
 * la gamme et le bon : une règle ordinaire, jamais une page nommée.
 */
export function cssPageFiche(): string {
  return [
    '@page {',
    '  size: A4 portrait;',
    `  margin: ${MARGES_FEUILLE_FICHE};`,
    `  @bottom-right { content: 'Page ' counter(page) ' / ' counter(pages); font-family: ${POLICE_DES_MARGES}; font-size: 9px; color: #666; }`,
    '}',
  ].join('\n');
}

/** Ajoute la règle de la fiche à la page et rend la fonction qui la retire : elle n'existe que tant que la fiche est affichée. */
export function installerPageFiche(doc: Document): () => void {
  const style = doc.createElement('style');
  style.setAttribute('data-impression-fiche', '');
  style.media = 'print';
  style.textContent = cssPageFiche();
  doc.head.appendChild(style);
  return () => style.remove();
}

/**
 * Ajoute la règle du bon à la page et rend la fonction qui la retire (même principe que `installerPageGamme`) : elle
 * n'existe que tant que la page d'impression du bon est affichée, les étiquettes QR gardent leurs marges à 0.
 */
export function installerPageBon(doc: Document, valide: boolean): () => void {
  const style = doc.createElement('style');
  style.setAttribute('data-impression-bon', '');
  style.media = 'print';
  style.textContent = cssPageBon(valide);
  doc.head.appendChild(style);
  return () => style.remove();
}

/**
 * Ajoute la règle à la page et rend la fonction qui la retire. À appeler quand la page d'impression s'affiche,
 * et à rappeler au démontage : sinon les marges de la gamme s'appliqueraient aux autres impressions.
 * L'élément est ajouté EN DERNIER dans `<head>` : à sélecteur égal, la dernière règle `@page` l'emporte sur
 * celle de la feuille de styles de l'application.
 */
export function installerPageGamme(doc: Document, interne: boolean): () => void {
  const style = doc.createElement('style');
  style.setAttribute('data-impression-gamme', '');
  style.media = 'print';
  style.textContent = cssPageGamme(interne);
  doc.head.appendChild(style);
  return () => style.remove();
}
